import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Mail, MessageSquare, Send } from "lucide-react";

import { fetchEventById } from "@/lib/api/events";
import {
  fetchParticipantById,
  updatePitchStatus,
  type ParticipantRow,
} from "@/lib/api/participants";
import { getOrganizerVideoUrl, triggerMatchScoring } from "@/lib/api/organizer-actions";
import { enqueueMeetupEmail, meetupEventUrl } from "@/lib/api/meetup-emails";
import { fetchMatches } from "@/lib/api/likes";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { PageHeadline } from "@/components/ui/Headline";
import { Loader, InlineAlert } from "@/components/ui/States";
import { Avatar } from "@/components/ui/Avatar";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

export function ParticipantDetailPage() {
  const { eventId, participantId } = useParams<{ eventId: string; participantId: string }>();
  const queryClient = useQueryClient();

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => fetchEventById(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const { data: participant, isLoading } = useQuery({
    queryKey: ["participant", participantId],
    queryFn: () => fetchParticipantById(participantId ?? ""),
    enabled: Boolean(participantId),
  });

  const { data: matches = [] } = useQuery({
    queryKey: ["participant-matches", participantId],
    queryFn: () => fetchMatches(event?.id ?? "", participantId ?? ""),
    enabled: Boolean(event?.id && participantId),
  });

  const { data: videoUrl } = useQuery({
    queryKey: ["pitch-video", participantId, participant?.video_storage_path],
    queryFn: () =>
      participant?.video_storage_path
        ? getOrganizerVideoUrl(participant.video_storage_path)
        : Promise.resolve(""),
    enabled: Boolean(participant?.video_storage_path),
    staleTime: 4 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: async (status: ParticipantRow["pitch_status"]) => {
      await updatePitchStatus(participantId ?? "", status);
      if (status === "approved") void triggerMatchScoring(participantId ?? "");
      if (status === "rejected" && event?.slug && participant) {
        void enqueueMeetupEmail({
          eventId: event.id,
          toEmail: participant.email,
          toName: participant.name,
          emailType: "meetup_pitch_declined",
          participantId: participant.id,
          payload: {
            name: participant.name,
            eventTitle: event.title,
            eventUrl: meetupEventUrl(event.slug),
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participant", participantId] });
      queryClient.invalidateQueries({ queryKey: ["participants", eventId] });
    },
  });

  const sendReminder = async () => {
    if (!event?.slug || !participant) return;
    await enqueueMeetupEmail({
      eventId: event.id,
      toEmail: participant.email,
      toName: participant.name,
      emailType: "meetup_invitation",
      participantId: participant.id,
      payload: {
        name: participant.name,
        eventTitle: event.title,
        eventUrl: meetupEventUrl(event.slug),
      },
    });
    alert("Reminder queued — will send within a minute.");
  };

  if (isLoading || !participant || !event) {
    return (
      <OrganizerShell>
        <Loader label="Loading participant..." />
      </OrganizerShell>
    );
  }

  const profile = participant.profile_json as
    | { headline?: string; about?: string; tags?: string[]; looking_for?: string; can_help_with?: string }
    | null;
  const flags = participant.ai_flags ?? [];
  const needsReview =
    participant.pitch_status === "flagged" || participant.pitch_status === "submitted";
  const statusTone =
    participant.pitch_status === "approved" ? "success" :
    participant.pitch_status === "flagged" ? "warning" :
    participant.pitch_status === "rejected" ? "danger" :
    participant.pitch_status === "submitted" ? "info" :
    "neutral";

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[820px] animate-fade-in">
        <Link
          to={`/events/${eventId}`}
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#475569] hover:text-[#2563eb]"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>

        <Card padding="md" className="mt-4">
          <div className="flex flex-wrap items-center gap-4">
            <VideoAvatar
              name={participant.name}
              size={80}
              bordered
              videoStoragePath={participant.video_storage_path}
              mode="organizer"
              showPlayBadge
            />
            <div className="flex-1">
              <PageHeadline title={participant.name} />
              <p className="mt-1 text-[13px] text-[#6c757d]">
                {[participant.role, participant.company].filter(Boolean).join(" · ") ||
                  participant.email}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge tone={statusTone}>{participant.pitch_status}</Badge>
                <Badge tone="info">
                  {matches.length} match{matches.length === 1 ? "" : "es"}
                </Badge>
              </div>
            </div>
            <Button
              variant="secondary"
              size="md"
              leadingIcon={<Send className="h-4 w-4" />}
              onClick={sendReminder}
            >
              Send reminder
            </Button>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <ContactRow icon={<Mail className="h-3.5 w-3.5" />} label="Email" value={participant.email} />
            <ContactRow
              icon={<MessageSquare className="h-3.5 w-3.5" />}
              label="Contact for matches"
              value={participant.contact_for_matches ?? "—"}
            />
          </div>
        </Card>

        {participant.video_storage_path ? (
          <Card padding="md" className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
              Pitch video
            </p>
            <div className="mt-4 flex justify-center">
              {videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  playsInline
                  className="aspect-square w-full max-w-[320px] rounded-full border-2 border-[#2563eb] object-cover"
                />
              ) : (
                <div className="grid h-[200px] w-[200px] place-items-center rounded-full bg-[#f8fafc] text-[13px] text-[#6c757d]">
                  Loading video...
                </div>
              )}
            </div>

            {flags.length > 0 ? (
              <Card tone="flagged" padding="sm" className="mt-4">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">AI flags</p>
                <ul className="mt-1 space-y-0.5">
                  {flags.map((f) => (
                    <li key={f} className="text-[13px] text-[#78350f]">
                      · {f}
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            {participant.transcription ? (
              <details className="mt-4">
                <summary className="cursor-pointer text-[13px] font-semibold text-[#2563eb]">
                  Transcript
                </summary>
                <div className="mt-2 max-h-[240px] overflow-y-auto rounded-[10px] bg-[#f8fafc] p-4 text-[13px] leading-relaxed text-[#475569]">
                  {participant.transcription}
                </div>
              </details>
            ) : null}

            {needsReview ? (
              <div className="mt-5 flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => mutation.mutate("rejected")}
                  loading={mutation.isPending}
                  fullWidth
                  size="md"
                >
                  Reject & Request Re-record
                </Button>
                <Button
                  onClick={() => mutation.mutate("approved")}
                  loading={mutation.isPending}
                  fullWidth
                  size="md"
                >
                  Approve Pitch
                </Button>
              </div>
            ) : null}
          </Card>
        ) : (
          <InlineAlert tone="warning" className="mt-5">
            No pitch recorded yet — send a reminder to nudge the participant.
          </InlineAlert>
        )}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <GoalCard
            title="Looking for"
            value={participant.looking_for || profile?.looking_for || ""}
          />
          <GoalCard
            title="Can help with"
            value={participant.can_help_with || profile?.can_help_with || ""}
          />
        </div>

        {profile?.about ? (
          <Card padding="md" className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
              AI profile — About
            </p>
            <p className="mt-2 text-[14px] text-[#475569] leading-relaxed">{profile.about}</p>
            {profile.tags && profile.tags.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {profile.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] px-2.5 py-1 text-[12px] font-semibold text-[#475569]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </Card>
        ) : null}

        {matches.length > 0 ? (
          <Card padding="md" className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
              Mutual matches ({matches.length})
            </p>
            <ul className="mt-3 space-y-2">
              {matches.map((m) => (
                <li key={m.match_id} className="flex items-center gap-3">
                  <VideoAvatar
                    name={m.other_name}
                    size={36}
                    videoStoragePath={m.other_video_storage_path}
                    mode="organizer"
                  />
                  <Link
                    to={`/events/${eventId}/participants/${m.other_id}`}
                    className="text-[14px] font-semibold text-[#2563eb] hover:underline"
                  >
                    {m.other_name}
                  </Link>
                  <span className="text-[13px] text-[#6c757d]">
                    {[m.other_role, m.other_company].filter(Boolean).join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </OrganizerShell>
  );
}

function ContactRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
        {icon}
        {label}
      </p>
      <p className="mt-1 select-all break-all text-[14px] text-[#1a1a1a]">{value}</p>
    </div>
  );
}

function GoalCard({ title, value }: { title: string; value: string }) {
  return (
    <Card padding="md">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">{title}</p>
      <p className="mt-2 text-[14px] text-[#475569] leading-relaxed">{value || "—"}</p>
    </Card>
  );
}
